/**
 * BehaviorCommitCommand
 *
 * Captures before/after BehaviorData snapshots around behavior.commit().
 * Supports coalescing so rapid field edits on the same field merge into one undo entry.
 */

import type { UndoCommand, UndoOptions, SerializedCommand } from './UndoCommand';
import type { BehaviorData } from '../../scenedata/behaviordata';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import { dispatchBehaviorChanged } from './editor-events';
import { registerCommandCodec } from './CommandCodec';

const COALESCE_WINDOW_MS = 800;
const CODEC_TYPE = 'BehaviorCommit';

interface BehaviorCommitPayload {
    objectPath: string;
    behaviorType: string;
    before: BehaviorData;
    after: BehaviorData;
    fieldKey: string | null;
}

export class BehaviorCommitCommand implements UndoCommand {
    readonly label: string;
    /**
     * Shared identity ref for the target SceneObject. Acquired from the
     * scene's identity registry at construction so that if a sibling
     * Create/Delete command later rebinds the underlying instance, this
     * command's undo/redo automatically retargets the live instance.
     */
    private identity: SceneObjectRef;
    private behaviorType: string;
    private before: BehaviorData;
    private after: BehaviorData;
    private fieldKey: string | null;
    private timestamp: number;
    /**
     * Path captured at construction. Used only by `serialize()` — once the
     * underlying instance is destroyed we have no live SceneObject to ask
     * for its path, so we stash it up-front.
     */
    private pathAtConstruction: string;

    constructor(
        sceneObject: SceneObject,
        behaviorType: string,
        before: BehaviorData,
        after: BehaviorData,
        fieldKey: string | null = null,
    ) {
        this.identity = sceneObject.traceScene.objectIdentityRegistry.refFor(sceneObject);
        this.behaviorType = behaviorType;
        this.before = before;
        this.after = after;
        this.fieldKey = fieldKey;
        this.timestamp = Date.now();
        this.pathAtConstruction = sceneObject.getPath();

        this.label = `Edit ${behaviorType} on ${sceneObject.name}`;
    }

    async undo(options?: UndoOptions): Promise<void> {
        const obj = this.identity.get();
        if (!obj) return;
        const behavior = obj.getBehavior(this.behaviorType);
        if (!behavior) return;

        // `commitSnapshot()` replaces authored data wholesale, including
        // dropping keys present now but missing from the pre-edit snapshot.
        // We need that for undo: if the forward edit *added* a field (e.g.
        // toggled noise from `undefined` to a noise object), a plain
        // `commit(patch)` would shallow-merge and leave the new field in
        // place because the snapshot doesn't have the key to overwrite it.
        await behavior.commitSnapshot(this.before);
        if (options?.silent) return;
        obj.traceScene?.select(obj);
        dispatchBehaviorChanged({
            objectPath: obj.getPath(),
            behaviorType: this.behaviorType,
            action: 'updated',
        });
    }

    async redo(): Promise<void> {
        const obj = this.identity.get();
        if (!obj) return;
        const behavior = obj.getBehavior(this.behaviorType);
        if (!behavior) return;

        // Snapshot restore — see `undo()` for the rationale.
        await behavior.commitSnapshot(this.after);
        obj.traceScene?.select(obj);
        dispatchBehaviorChanged({
            objectPath: obj.getPath(),
            behaviorType: this.behaviorType,
            action: 'updated',
        });
    }

    serialize(): SerializedCommand {
        // Prefer the live path if the underlying instance still exists
        // (it may have been renamed since construction). Fall back to the
        // captured path when the instance has been destroyed.
        const obj = this.identity.get();
        const payload: BehaviorCommitPayload = {
            objectPath: obj ? obj.getPath() : this.pathAtConstruction,
            behaviorType: this.behaviorType,
            before: structuredClone(this.before),
            after: structuredClone(this.after),
            fieldKey: this.fieldKey,
        };
        return { type: CODEC_TYPE, data: payload };
    }

    coalesce(other: UndoCommand): boolean {
        if (!(other instanceof BehaviorCommitCommand)) return false;
        if (other.identity !== this.identity) return false;
        if (other.behaviorType !== this.behaviorType) return false;
        if (this.fieldKey === null || other.fieldKey === null) return false;
        if (other.fieldKey !== this.fieldKey) return false;
        if (other.timestamp - this.timestamp > COALESCE_WINDOW_MS) return false;

        this.after = other.after;
        // Sliding window: updating the timestamp to the newer entry means
        // continuous editing (e.g. holding down a key, dragging a slider)
        // keeps merging indefinitely as long as consecutive edits fall
        // within COALESCE_WINDOW_MS of each other. Pausing longer than
        // the window breaks the chain, so the next edit starts a new
        // undo entry. This matches typical text-editor "one entry per
        // word" behavior.
        this.timestamp = other.timestamp;
        return true;
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): BehaviorCommitCommand => {
    const p = data as BehaviorCommitPayload;
    const obj = scene.resolveSceneObject(p.objectPath);
    if (!obj) {
        throw new Error(`[BehaviorCommitCommand] Cannot resolve path "${p.objectPath}"`);
    }
    return new BehaviorCommitCommand(obj, p.behaviorType, p.before, p.after, p.fieldKey);
});
